// Mobile dashboard header and published framing rows. The shared comms
// wrapper supplies responsive review/delivery, digest and reminder forms;
// unpublished framing edits remain in the parent across width changes.

import { ManifestTools } from './ManifestTools';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { Manifest } from '../../shared/manifest-types';
import { FieldRow } from './FieldSheet';
import { BackIcon } from './icons';
import { formatDate } from '../lib/calendar';
import { dashboardPhaseLine, dashboardShortUrl } from '../lib/dashboardMeta';
import { DASHBOARD_BASE, DASHBOARD_PHASES } from '../pages/Dashboards';

export interface DashboardEditMobileFields {
  current_phase: string;
  contact_name: string;
  contact_email: string;
  show_production: boolean;
  show_review: boolean;
  show_commercial: boolean;
}

export function DashboardEditMobile({
  slug,
  manifest,
  fields,
  pending,
  busy,
  error,
  onPublish,
  onToolsApplied,
  comms,
}: {
  slug: string;
  manifest: Manifest;
  /** Published fields on navigation; preserves an existing desktop draft on resize. */
  fields: DashboardEditMobileFields;
  pending: number;
  busy: boolean;
  error: string | null;
  onPublish: () => void;
  onToolsApplied?: () => void | Promise<void>;
  comms?: (overview: ReactNode) => ReactNode;
}) {
  const linkedProjectName = manifest.project_name ?? null;
  const lastPublished = manifest.meta?.generated_at?.slice(0, 10) ?? null;
  const phaseStatus = manifest.phases?.find((p) => p.key === fields.current_phase)?.status ?? null;
  const contact = fields.contact_name || fields.contact_email || '—';

  const overview = (<>
      <div className="dash-m-fields">
        <FieldRow
          label="Internal preview"
          mono
          tone="acc"
          value={
            <a href={`${DASHBOARD_BASE}/${slug}?internal=1`} target="_blank" rel="noreferrer">
              {dashboardShortUrl(slug)} ↗
            </a>
          }
        />
        <FieldRow label="Phase" value={dashboardPhaseLine(DASHBOARD_PHASES, fields.current_phase, phaseStatus)} />
        <FieldRow label="Contact" value={contact} />
        <FieldRow
          label="Sections"
          value={
            <span className="dash-m-sections">
              {(['production', 'review', 'commercial'] as const).map((key) => (
                <span key={key} className={`pill${fields[`show_${key}` as const] ? ' accent' : ''}`}>
                  {key}
                </span>
              ))}
            </span>
          }
        />
        <FieldRow label="Last published" mono value={lastPublished ? formatDate(lastPublished) : '—'} />
      </div>


    {/* ManifestTools insertion: slug, onApplied refresh, disabled while unpublished edits exist */}
  </>);

  return (
    <div className="dash-m-edit">
      <div className="dash-m-head">
        <Link className="dash-m-back" to="/dashboards">
          <BackIcon />
          Dashboards
        </Link>
        <h1>{manifest.client_name}</h1>
        <div className="dash-m-sub">
          Client-facing progress page{linkedProjectName ? ` · linked to ${linkedProjectName}` : ''}
        </div>
      </div>

      {error && <p className="error-text dash-m-error">{error}</p>}

      {pending > 0 && (
        <div className="dash-m-banner warn">
          <div className="k">Unpublished</div>
          <div className="t">
            {pending} change{pending === 1 ? '' : 's'} not yet live — clients won’t see them until
            you publish.
          </div>
          <div className="chips">
            <button type="button" className="cbtn pri" onClick={onPublish} disabled={busy}>
              {busy ? 'Publishing…' : 'Publish now →'}
            </button>
          </div>
        </div>
      )}

      <div className="cc-edit-mobile">{comms ? comms(overview) : overview}</div>
      {/* ManifestTools insertion: slug, onApplied refresh, disabled while unpublished edits exist */}

      {onToolsApplied && <ManifestTools slug={slug} onApplied={onToolsApplied} disabled={pending > 0 || busy} />}

    </div>
  );
}
